import { PuzzleEngine } from "../puzzle-engine.ts";
import { calculateScore } from "../score-service.ts";
import { bestRecordKey, StorageService } from "../storage-service.ts";
import { HintExhaustedError, StorageReadError, type PixelPoint, type PuzzlePiece } from "../types.ts";
import { lang, mmss, stars, t } from "./i18n.ts";
import { MapView } from "./map-view.ts";

const SVG_NS = "http://www.w3.org/2000/svg";
const nameOf = (p: PuzzlePiece) => (lang() === "en" ? p.countryNameEn : p.countryName);
// 實際顏色定義在 CSS 的 .pc0~.pc7，切換主題時整批跟著換，不需重新渲染圖層
const PALETTE_SIZE = 8;

interface Elements {
  tray: HTMLElement;
  timer: HTMLElement;
  progress: HTMLElement;
  mistakes: HTMLElement;
  hintButton: HTMLButtonElement;
  hintLabel: HTMLElement;
  pauseButton: HTMLButtonElement;
  moreButton: HTMLButtonElement;
  morePanel: HTMLElement;
  pauseIcon: SVGUseElement;
  progressBar: HTMLElement;
  resumeButton: HTMLButtonElement;
  quitButton: HTMLButtonElement;
  result: HTMLElement;
}

export interface GameCallbacks {
  onPlayAgain: () => void;
  onBackToMenu: () => void;
}

export class GameView {
  private readonly engine: PuzzleEngine;
  private readonly mapView: MapView;
  private readonly el: Elements;
  private readonly storage: StorageService | null;
  private readonly callbacks: GameCallbacks;
  private readonly colours = new Map<string, string>();
  // HUD 按鈕存在於 HTML 不會隨局數重建，若不統一解除，每開一局就會多疊一層監聽器
  private readonly listeners = new AbortController();
  private drag: { piece: PuzzlePiece; item: HTMLElement } | null = null;
  private ticker = 0;

  constructor(
    engine: PuzzleEngine,
    mapView: MapView,
    el: Elements,
    storage: StorageService | null,
    callbacks: GameCallbacks,
  ) {
    this.engine = engine;
    this.mapView = mapView;
    this.el = el;
    this.storage = storage;
    this.callbacks = callbacks;

    engine.session.pieces.forEach((p, i) => this.colours.set(p.pieceId, `pc${i % PALETTE_SIZE}`));
    mapView.fitTo(engine.session.pieces);
    mapView.showGuideOutlines(engine.session.pieces);

    this.renderTray();
    this.bindControls();
    this.ticker = window.setInterval(() => this.renderHud(), 250);
    this.renderHud();
  }

  private unlockedPieces(): PuzzlePiece[] {
    const byId = new Map(this.engine.session.pieces.map((p) => [p.pieceId, p]));
    return this.engine.session.trayOrder.map((id) => byId.get(id)!).filter(Boolean);
  }

  private renderTray(): void {
    this.el.tray.replaceChildren();
    for (const piece of this.unlockedPieces()) {
      this.el.tray.appendChild(this.trayItem(piece));
    }
  }

  private trayItem(piece: PuzzlePiece): HTMLElement {
    const item = document.createElement("button");
    item.className = "tray-item";
    item.dataset.pieceId = piece.pieceId;
    item.title = nameOf(piece);

    const [minLng, minLat, maxLng, maxLat] = piece.bbox;
    const w = Math.max(maxLng - minLng, 1e-4);
    const h = Math.max(maxLat - minLat, 1e-4);

    const svg = document.createElementNS(SVG_NS, "svg");
    // 緯度向上、SVG y 向下，故以 -lat 作為 y 並把 viewBox 上緣設為 -maxLat
    svg.setAttribute("viewBox", `${minLng} ${-maxLat} ${w} ${h}`);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

    const path = document.createElementNS(SVG_NS, "path");
    let d = "";
    for (const polygon of piece.geometry.coordinates) {
      for (const ring of polygon) {
        ring.forEach(([lng, lat], i) => { d += `${i === 0 ? "M" : "L"}${lng} ${-lat}`; });
        d += "Z";
      }
    }
    path.setAttribute("d", d);
    path.setAttribute("class", this.colours.get(piece.pieceId)!);
    path.setAttribute("vector-effect", "non-scaling-stroke");
    svg.appendChild(path);

    const label = document.createElement("span");
    label.textContent = nameOf(piece);

    item.append(svg, label);
    item.addEventListener("pointerdown", (ev) => this.startDrag(piece, item, ev), {
      signal: this.listeners.signal,
    });
    return item;
  }

  private startDrag(piece: PuzzlePiece, item: HTMLElement, ev: PointerEvent): void {
    if (this.engine.session.status !== "Playing" || this.drag) return;
    ev.preventDefault();
    this.drag = { piece, item };
    item.classList.add("dragging");

    this.mapView.showSlots(this.engine.session.pieces.filter((p) => !p.isLocked));
    this.mapView.showGhost(piece, this.mapView.containerPoint(ev));
    const signal = this.listeners.signal;
    window.addEventListener("pointermove", this.onPointerMove, { signal });
    window.addEventListener("pointerup", this.onPointerUp, { signal });
  }

  private onPointerMove = (ev: PointerEvent): void => {
    if (!this.drag) return;
    this.mapView.moveGhost(this.mapView.containerPoint(ev));
  };

  private onPointerUp = (ev: PointerEvent): void => {
    if (!this.drag) return;
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);

    const { piece, item } = this.drag;
    item.classList.remove("dragging");

    this.mapView.hideSlots();

    // 放回托盤不算失誤，直接取消（SRS §5 模組 3 呼叫契約）
    if (!this.mapView.isOverMap(ev)) {
      this.mapView.hideGhost();
      this.drag = null;
      return;
    }

    const drop: PixelPoint = this.mapView.containerPoint(ev);
    const result = this.engine.evaluatePlacement(piece.pieceId, drop, this.mapView.projection);

    if (result.correct) {
      this.mapView.hideGhost();
      this.mapView.addLocked(piece, this.colours.get(piece.pieceId)!, nameOf(piece));
      this.mapView.removeGuideOutline(piece.pieceId);
      item.remove();
      this.drag = null;
      this.renderHud();
      if (result.completed) this.showResult();
      return;
    }

    this.mapView.flashGhostWrong();
    window.setTimeout(() => this.mapView.hideGhost(), 220);
    this.drag = null;
    this.renderHud();
  };

  private bindControls(): void {
    const signal = this.listeners.signal;
    this.el.hintButton.addEventListener("click", () => {
      const next = this.unlockedPieces()[0];
      if (!next) return;
      try {
        this.engine.useHint(next.pieceId);
        this.mapView.focusOn(next);
        this.mapView.pulseTarget(next);
        this.el.tray.querySelector<HTMLElement>(`[data-piece-id="${CSS.escape(next.pieceId)}"]`)
          ?.classList.add("hinted");
      } catch (err) {
        if (!(err instanceof HintExhaustedError)) throw err;
        this.el.hintButton.disabled = true;
      }
      this.renderHud();
    }, { signal });

    const togglePause = () => {
      const s = this.engine.session;
      if (s.status === "Playing") this.engine.pause();
      else if (s.status === "Paused") this.engine.resume();
      document.body.classList.toggle("paused", this.engine.session.status === "Paused");
      this.renderHud();
    };
    this.el.pauseButton.addEventListener("click", togglePause, { signal });
    this.el.resumeButton.addEventListener("click", togglePause, { signal });

    const closeMenu = () => {
      this.el.morePanel.hidden = true;
      this.el.moreButton.setAttribute("aria-expanded", "false");
    };
    this.el.moreButton.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const open = this.el.morePanel.hidden;
      this.el.morePanel.hidden = !open;
      this.el.moreButton.setAttribute("aria-expanded", String(open));
    }, { signal });
    // 選完就收起來；點外面或按 Esc 也收
    this.el.morePanel.addEventListener("click", closeMenu, { signal });
    document.addEventListener("click", closeMenu, { signal });
    document.addEventListener("keydown", (ev) => { if (ev.key === "Escape") closeMenu(); }, { signal });

    // 只在暫停時才能放棄，等於用既有的暫停動作當作二次確認，不必再跳確認框
    this.el.quitButton.addEventListener("click", () => {
      if (this.engine.session.status === "Paused") this.engine.abandon();
      document.body.classList.remove("paused");
      this.callbacks.onBackToMenu();
    }, { signal });
  }

  private renderHud(): void {
    const s = this.engine.session;
    this.el.timer.textContent = mmss(this.engine.elapsedSeconds());
    this.el.progress.textContent = `${this.engine.lockedCount} / ${s.pieces.length}`;
    this.el.mistakes.textContent = String(s.mistakeCount);

    const done = this.engine.lockedCount;
    this.el.progressBar.style.width = `${(done / s.pieces.length) * 100}%`;

    const limit = this.engine.config.hintLimit;
    const left = limit === Infinity ? "∞" : String(Math.max(0, limit - s.hintUsedCount));
    // 按鈕內含 SVG，只能改文字子節點，不能整顆 textContent 蓋掉
    this.el.hintLabel.textContent = t("hint", { n: left });
    this.el.hintButton.disabled = limit !== Infinity && s.hintUsedCount >= limit;

    const paused = s.status === "Paused";
    this.el.pauseButton.title = t(paused ? "resume" : "pause");
    this.el.pauseIcon.setAttribute("href", paused ? "#i-play" : "#i-pause");
  }

  /** 本地紀錄的成敗不得影響本局分數顯示（SRS AC-3.2） */
  private persistScore(score: ReturnType<typeof calculateScore>) {
    const s = this.engine.session;
    const key = bestRecordKey(s.regionScope, s.difficulty);
    if (!this.storage) return { isNewRecord: false, previous: null, warning: "此瀏覽器無法儲存本地紀錄" };

    try {
      return { ...this.storage.saveIfBetter(key, score), warning: "" };
    } catch (err) {
      if (err instanceof StorageReadError) {
        try {
          this.storage.reset();
          return { ...this.storage.saveIfBetter(key, score), warning: t("storageReset") };
        } catch { /* 落到下方統一處理 */ }
      }
      return { isNewRecord: false, previous: null, warning: t("storageWriteFailed") };
    }
  }

  private showResult(): void {
    window.clearInterval(this.ticker);
    const s = this.engine.session;
    const score = calculateScore(s);
    const { isNewRecord, previous, warning } = this.persistScore(score);

    const card = document.createElement("div");
    card.className = "card";

    const add = (tag: string, text: string, cls?: string) => {
      const node = document.createElement(tag);
      node.textContent = text;
      if (cls) node.className = cls;
      card.appendChild(node);
      return node;
    };

    add("h2", t("complete"));
    add("div", stars(score.stars), "stars");
    add("div", String(score.finalScore), "score");
    add("p", t("resultTime", { percent: score.percentage, time: mmss(score.elapsedSeconds) }));
    add("p", t("resultStats", { pieces: s.pieces.length, mistakes: score.mistakeCount, hints: score.hintUsedCount }));
    if (isNewRecord) add("p", t("newRecord"), "record");
    if (previous) add("p", t("previousBest", { score: previous.bestScore, time: mmss(previous.bestTimeSeconds) }));
    if (warning) add("p", warning, "warning");

    const buttons = document.createElement("div");
    buttons.className = "actions";
    for (const [label, handler] of [
      [t("playAgain"), this.callbacks.onPlayAgain],
      [t("quit"), this.callbacks.onBackToMenu],
    ] as const) {
      const button = document.createElement("button");
      button.textContent = label;
      button.addEventListener("click", handler);
      buttons.appendChild(button);
    }
    card.appendChild(buttons);

    // 結算的署名直接複製主選單那顆，文案與樣式只需維護一份
    const stack = document.createElement("div");
    stack.className = "centered-stack";
    stack.appendChild(card);
    const credit = document.querySelector(".site-credit");
    if (credit) stack.appendChild(credit.cloneNode(true));

    this.el.result.replaceChildren(stack);
    this.el.result.classList.add("visible");
  }

  /** 語言切換後呼叫：托盤的國名與 HUD 文字都要重生成 */
  refresh(): void {
    // 重建托盤會砍掉正在拖曳的元素，故先把進行中的拖曳收乾淨（不計失誤）
    if (this.drag) {
      this.mapView.hideGhost();
      this.mapView.hideSlots();
      this.drag = null;
    }
    this.renderTray();
    this.renderHud();
    this.mapView.relabelLocked(nameOf);
  }

  destroy(): void {
    this.el.morePanel.hidden = true;
    window.clearInterval(this.ticker);
    this.listeners.abort();
    this.el.tray.replaceChildren();
    this.el.result.replaceChildren();
    this.el.result.classList.remove("visible");
    document.body.classList.remove("paused");
  }
}
